// frontend/src/hooks/usePttSimulation.ts
//
// Software-in-the-Loop (SiL) — symulacja sprzętowego przycisku Push-To-Talk.
//
// Fizyczny PTT to gruszka USB / przycisk słuchawki Bluetooth (patrz
// `presentation/hooks/useBluetoothButton`). Na biurku dewelopera zwykle jej nie
// ma, a cały tor „wciśnij → dyktuj → puść → asystent AI" trzeba jakoś przeklikać.
// Ten hook mapuje PRZYTRZYMANIE klawisza (domyślnie F1 lub Spacja) na tę samą
// parę callbacków start/stop, której używa sprzętowy przycisk:
//
//   keydown (klawisz z `keys`)      → onPress    (jak wciśnięcie gruszki)
//   keyup   (klawisz z `keys`)      → onRelease  (jak puszczenie gruszki)
//   blur okna w trakcie trzymania   → onRelease  (bezpiecznik „zaciętego" PTT)
//
// Zasady spójne z `useBluetoothButton`:
//   • callbacki czytane przez ref — re-render rodzica nie odpina nasłuchu,
//   • `pressedRef` jest źródłem prawdy: `onPress`/`onRelease` są idempotentne,
//     więc autopowtarzanie klawisza (seria zdarzeń `keydown`) woła `onPress` raz,
//   • bezpieczny w środowiskach bez DOM (SSR) — wtedy nasłuch po prostu nie
//     powstaje,
//   • funkcja czyszcząca zdejmuje wszystkie nasłuchy.
//
// Domyślnie aktywny TYLKO w trybie deweloperskim (`import.meta.env.DEV`);
// w produkcyjnym buildzie `enabled === false` i hook nie podpina niczego.

import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Klawisze (`KeyboardEvent.code` LUB `.key`) domyślnie symulujące wciśnięcie PTT.
 *
 * `F1` / `Space` to warianty biurkowe. Reszta to prewencyjne pokrycie
 * sprzętowego przycisku na tablecie z Androidem — zewnętrzna gruszka / pilot BT
 * najczęściej melduje się jako media-key, a nie „zwykły" klawisz:
 *   • `MediaPlayPause`  — pilot słuchawkowy / BT „play/pauza",
 *   • `VolumeUp` / `VolumeDown` (starsza pisownia) oraz `AudioVolumeUp` /
 *     `AudioVolumeDown` (nowsza, UI Events) — klawisze głośności,
 *   • `HeadsetHook`     — przycisk „odbierz/rozłącz" na kablu zestawu.
 * Faktyczny kod z konkretnego tabletu podejrzyj SNIFFEREM z `App.tsx`
 * i dopisz tutaj, jeśli okaże się inny.
 */
export const DEFAULT_PTT_SIMULATION_KEYS = [
  'F1',
  'Space',
  'MediaPlayPause',
  'VolumeUp',
  'VolumeDown',
  'HeadsetHook',
  'AudioVolumeUp',
] as const

/**
 * Aliasy tokenów klawiszy — sprowadzamy do JEDNEJ, kanonicznej postaci po obu
 * stronach porównania (zbiór `keys` i pole zdarzenia).
 *
 *   • `' '` → `'Space'`: `KeyboardEvent.key` dla spacji to pojedynczy znak spacji
 *     (NIE `'Space'`). Bez tego gałąź „dopasuj po `key`" jest dla spacji martwa,
 *     więc spacja trafiałaby wyłącznie przez `event.code` — a ten bywa pusty /
 *     `'Unidentified'` (zdalne pulpity, klawiatury ekranowe, IME, część zdarzeń
 *     syntetycznych) i wtedy nasłuch spacji znika, choć F1 działa dalej.
 *   • `AudioVolume*` → `Volume*`: Android/Chrome bywa niespójny — starsza
 *     specyfikacja to `VolumeUp`/`VolumeDown`, nowsza (UI Events) to
 *     `AudioVolumeUp`/`AudioVolumeDown`. Zwijamy obie pisownie do jednej, żeby
 *     dopasowanie działało niezależnie od tego, którą wersję zwróci dany WebView.
 */
const KEY_TOKEN_ALIASES: Readonly<Record<string, string>> = {
  ' ': 'Space',
  AudioVolumeUp: 'VolumeUp',
  AudioVolumeDown: 'VolumeDown',
  AudioVolumeMute: 'VolumeMute',
}

/** Sprowadza token klawisza (`event.code` / `event.key` / wpis w `keys`) do postaci kanonicznej. */
export function normalizeKeyToken(token: string): string {
  return KEY_TOKEN_ALIASES[token] ?? token
}

/** Nazwy tagów, które traktujemy jako pola edycyjne (Spacja = wpisywany znak). */
const EDITABLE_TAGS: ReadonlySet<string> = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

/** Konfiguracja symulatora PTT. Wymagane są tylko `onPress` i `onRelease`. */
export interface UsePttSimulationOptions {
  /** Wołane przy wciśnięciu klawisza symulacji (odpowiednik wciśnięcia gruszki). */
  onPress: () => void
  /** Wołane przy puszczeniu klawisza symulacji (odpowiednik puszczenia gruszki). */
  onRelease: () => void
  /**
   * Główny włącznik warstwy symulacji. Domyślnie `import.meta.env.DEV`
   * (tryb deweloperski Vite) — w produkcyjnym buildzie hook jest no-opem.
   */
  enabled?: boolean
  /**
   * Klawisze wyzwalające — porównywane zarówno z `KeyboardEvent.code`, jak i
   * `KeyboardEvent.key`. Tokeny są normalizowane {@link normalizeKeyToken}
   * (`' '` ≡ `'Space'`, `AudioVolumeUp` ≡ `VolumeUp`, …), więc działają
   * niezależnie od tego, czy podasz starą czy nową pisownię i czy zdarzenie
   * przyniesie `code`, czy tylko `key`. Domyślnie
   * {@link DEFAULT_PTT_SIMULATION_KEYS} (F1 / Spacja + media-keys Androida).
   */
  keys?: readonly string[]
  /**
   * Pomijaj `keydown` pochodzący z pól edycyjnych (`input`, `textarea`,
   * `select`, `contenteditable`), żeby Spacja wpisywana w notatce nie
   * uruchamiała PTT. Domyślnie `true`. (Dotyczy tylko startu — `keyup` zawsze
   * może zakończyć wciśnięcie.)
   */
  ignoreEditableTargets?: boolean
  /**
   * Pomijaj `keydown` z wciśniętym Ctrl/Alt/Meta, żeby nie przechwytywać
   * skrótów systemu / przeglądarki. Domyślnie `true`.
   */
  ignoreWithModifiers?: boolean
  /**
   * Cel nasłuchu zdarzeń klawiatury. Domyślnie `window` (a w środowisku bez
   * `window` — brak nasłuchu). Przekaż `null`, aby świadomie wyłączyć wpięcie.
   */
  target?: Window | null
}

/** Uchwyt zwracany przez {@link usePttSimulation} — stan + sterowanie programowe. */
export interface PttSimulationController {
  /** `true`, dopóki klawisz symulacji jest trzymany (odpowiednik „PTT wciśnięty"). */
  isPressed: boolean
  /** Czy warstwa symulacji jest skonfigurowana jako aktywna (opcja `enabled`). */
  isEnabled: boolean
  /**
   * Programowe wciśnięcie PTT (idempotentne). Działa niezależnie od `enabled`
   * i `target` — to jawne wywołanie, przydatne w testach i nakładce debug.
   */
  press: () => void
  /** Programowe puszczenie PTT (idempotentne). */
  release: () => void
}

/** Czy zdarzenie pochodzi z elementu, w którym użytkownik wpisuje tekst. */
function isFromEditableTarget(target: EventTarget | null): boolean {
  const el = target as (Partial<HTMLElement> & { tagName?: unknown }) | null
  if (el == null || typeof el.tagName !== 'string') return false
  if (EDITABLE_TAGS.has(el.tagName)) return true
  if (el.isContentEditable === true) return true
  const attr =
    typeof el.getAttribute === 'function' ? el.getAttribute('contenteditable') : null
  return attr === '' || attr === 'true' || attr === 'plaintext-only'
}

/**
 * Symuluje sprzętowy przycisk PTT klawiaturą (Software-in-the-Loop). Zwraca
 * stan wciśnięcia oraz programowe `press`/`release` (np. dla nakładki debug).
 *
 * @example
 * ```ts
 * // Spina symulację z tym samym torem, co fizyczna gruszka / słuchawka.
 * usePttSimulation({
 *   onPress: handleBluetoothStart,
 *   onRelease: handleBluetoothStop,
 * })
 * ```
 */
export function usePttSimulation({
  onPress,
  onRelease,
  enabled = import.meta.env.DEV,
  keys = DEFAULT_PTT_SIMULATION_KEYS,
  ignoreEditableTargets = true,
  ignoreWithModifiers = true,
  target,
}: UsePttSimulationOptions): PttSimulationController {
  // Świeże referencje callbacków — nasłuch montujemy raz, a re-render rodzica
  // nie może go odpinać (spójnie z `useBluetoothButton`).
  const onPressRef = useRef(onPress)
  const onReleaseRef = useRef(onRelease)
  onPressRef.current = onPress
  onReleaseRef.current = onRelease

  // Zbiór klawiszy i przełączniki trzymamy w refach, żeby efekt nie zależał od
  // tożsamości tablicy `keys` (nowa literalna tablica co render odpinałaby nasłuch).
  // Zbiór trzymamy w postaci znormalizowanej (`' '` → `'Space'`), żeby dopasowanie
  // po `event.key` działało dla spacji tak samo jak po `event.code`.
  const keySetRef = useRef<Set<string>>(new Set(Array.from(keys, normalizeKeyToken)))
  keySetRef.current = new Set(Array.from(keys, normalizeKeyToken))
  const guardsRef = useRef({ ignoreEditableTargets, ignoreWithModifiers })
  guardsRef.current = { ignoreEditableTargets, ignoreWithModifiers }

  // `pressedRef` — synchroniczne źródło prawdy dla idempotencji; `isPressed` to
  // jego reaktywne odbicie do UI (np. nakładka „PTT SIM ACTIVE").
  const pressedRef = useRef(false)
  const [isPressed, setIsPressed] = useState(false)

  const press = useCallback((): void => {
    if (pressedRef.current) return
    pressedRef.current = true
    setIsPressed(true)
    onPressRef.current()
  }, [])

  const release = useCallback((): void => {
    if (!pressedRef.current) return
    pressedRef.current = false
    setIsPressed(false)
    onReleaseRef.current()
  }, [])

  useEffect(() => {
    if (!enabled) return
    const view: Window | null =
      target === undefined ? (typeof window === 'undefined' ? null : window) : target
    if (view === null) return

    // Dopasuj po `event.code` ('Space', 'F1'…) LUB po `event.key`, OBA sprowadzone
    // do tej samej postaci co zbiór (`normalizeKeyToken`: `' '` → `'Space'`,
    // `AudioVolume*` → `Volume*`). Dzięki gałęzi po `key` spacja jest łapana nawet
    // gdy `event.code` przyjdzie pusty / 'Unidentified', a media-key z Androida
    // działa niezależnie od tego, czy sprzęt zapełni `code`, czy tylko `key`.
    const matchesKey = (event: KeyboardEvent): boolean =>
      keySetRef.current.has(normalizeKeyToken(event.code)) ||
      keySetRef.current.has(normalizeKeyToken(event.key))

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!matchesKey(event)) return
      const { ignoreEditableTargets: skipEditable, ignoreWithModifiers: skipMods } =
        guardsRef.current
      // Modyfikator wciśnięty → to skrót systemu / przeglądarki (Alt+F1, Cmd+Spacja…),
      // nie PTT. Puszczamy zdarzenie dalej BEZ `preventDefault`.
      if (skipMods && (event.ctrlKey || event.altKey || event.metaKey)) return
      // Zdarzenie z pola edycyjnego → Spacja to znak wpisywany do notatki. Ani
      // `preventDefault`, ani `stopPropagation`, ani `press()` — inaczej nie dałoby
      // się wstawić spacji w zwykłym polu tekstowym (input / textarea / CE).
      if (skipEditable && isFromEditableTarget(event.target)) return
      // ŻELAZNY WARUNEK: dopasowany klawisz PTT (domyślnie F1 ALBO Spacja) POZA
      // polem tekstowym — betonujemy go przed przeglądarką. Nasłuch działa w
      // FAZIE CAPTURE (patrz rejestracja niżej), więc jesteśmy pierwsi w łańcuchu:
      //   • `stopPropagation()` ubija event, zanim dojdzie do jakiegokolwiek
      //     handlera strony (fokusowany przycisk aktywowany Spacją, skrót, menu),
      //   • `preventDefault()` zdejmuje akcję domyślną przeglądarki (Spacja =
      //     scroll strony, F1 = okno pomocy).
      // Oba MUSZĄ lecieć również dla `event.repeat` — trzymany klawisz generuje
      // całą serię `keydown`, a przeglądarka wykonuje akcję domyślną na KAŻDYM z
      // nich (inaczej przez cały czas trzymania PTT strona „ucieka" w dół i gubi
      // fokus — zgłoszenie: „Spacja gubi focus / otwiera dziwne menu przeglądarki").
      event.preventDefault()
      event.stopPropagation()
      // Samego `press()` wystarczy użyć RAZ — na pierwszym (nie-`repeat`) `keydown`.
      // Jest idempotentny, ale nie ma po co wołać go na każdej klatce autopowtarzania.
      if (event.repeat) return
      press()
    }

    const handleKeyUp = (event: KeyboardEvent): void => {
      if (!matchesKey(event)) return
      // Bez filtra pól edycyjnych: `keyup` zawsze musi móc ZAKOŃCZYĆ wciśnięcie,
      // inaczej PTT „zacięłoby się" po puszczeniu klawisza nad innym elementem.
      // Symetrycznie do `keydown` betonujemy klawisz (faza capture + oba stopy),
      // żeby żaden handler strony nie zobaczył „gołej" Spacji / F1.
      event.preventDefault()
      event.stopPropagation()
      release()
    }

    // Utrata fokusu okna gubi `keyup` (klawisz zostaje „wciśnięty") — wymuszamy
    // puszczenie, żeby dyktowanie nie wisiało w nieskończoność.
    const handleBlur = (): void => {
      release()
    }

    // FAZA CAPTURE (`{ capture: true }`): keydown/keyup łapiemy w drodze W DÓŁ
    // drzewa, ZANIM event dojdzie do handlerów strony (przycisk reagujący na
    // Spację, natywny scroll, pomoc pod F1) i zanim przeglądarka zdąży wykonać
    // akcję domyślną / „ukraść" fokus. W fazie bąbelkowania byłoby już za późno.
    // `removeEventListener` MUSI dostać ten sam `{ capture: true }` — bez tego
    // nasłuch capture nie zostałby zdjęty przy cleanupie.
    view.addEventListener('keydown', handleKeyDown, { capture: true })
    view.addEventListener('keyup', handleKeyUp, { capture: true })
    view.addEventListener('blur', handleBlur)

    return () => {
      view.removeEventListener('keydown', handleKeyDown, { capture: true })
      view.removeEventListener('keyup', handleKeyUp, { capture: true })
      view.removeEventListener('blur', handleBlur)
      // Odmontowanie / wyłączenie w trakcie trzymania klawisza: domknij
      // wciśnięcie, żeby nie zostawić „wiszącego" dyktowania. (Świadoma różnica
      // względem `useBluetoothButton`, gdzie cleanup tylko zeruje flagę — tam
      // źródłem zdarzeń jest system, tu ulotny nasłuch klawiatury.)
      if (pressedRef.current) {
        pressedRef.current = false
        setIsPressed(false)
        onReleaseRef.current()
      }
    }
  }, [enabled, target, press, release])

  return { isPressed, isEnabled: enabled, press, release }
}
